import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import CategoryCombobox from './CategoryCombobox.jsx';
import Button from './Button.jsx';
import FormField from './FormField.jsx';
import {
  useCreateMappingRule,
  useUpdateMappingRule,
  useDeleteMappingRule,
} from '../hooks/useMappingRules.js';

const MATCH_TYPE_OPTIONS = [
  { value: 'label_contains', label: 'Libellé contient' },
  { value: 'beneficiary_equals', label: 'Bénéficiaire égal à' },
  { value: 'qonto_category', label: 'Catégorie Qonto' },
  { value: 'amount_min', label: 'Montant ≥ (€)' },
  { value: 'amount_max', label: 'Montant ≤ (€)' },
];

const EMPTY_CONDITION = { match_type: 'label_contains', match_value: '' };

function emptyConditions(rule) {
  if (rule?.conditions?.length) return rule.conditions.map((c) => ({ ...c }));
  return [{ ...EMPTY_CONDITION }];
}

export default function RuleEditor({ rule, onSave, onCancel, onDelete }) {
  const isEdit = Boolean(rule);
  const create = useCreateMappingRule();
  const update = useUpdateMappingRule();
  const remove = useDeleteMappingRule();

  const [categoryId, setCategoryId] = useState(rule?.category_id ?? '');
  const [operator, setOperator] = useState(rule?.operator ?? 'AND');
  const [conditions, setConditions] = useState(emptyConditions(rule));
  const [priority, setPriority] = useState(rule?.priority ?? 200);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const mutation = isEdit ? update : create;
  const isPending = mutation.isPending || remove.isPending;

  function addCondition() {
    if (conditions.length >= 10) return;
    setConditions((prev) => [...prev, { ...EMPTY_CONDITION }]);
  }

  function removeCondition(idx) {
    setConditions((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateCondition(idx, field, value) {
    setConditions((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c))
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!categoryId) return;
    const validConditions = conditions.filter((c) => c.match_value.trim() !== '');
    if (validConditions.length === 0) return;

    const payload = {
      category_id: categoryId,
      operator,
      conditions: validConditions,
      priority: Number(priority) || 200,
    };

    try {
      if (isEdit) {
        await update.mutateAsync({ id: rule.id, ...payload });
      } else {
        await create.mutateAsync(payload);
      }
      onSave?.();
    } catch {}
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await remove.mutateAsync(rule.id);
      onDelete?.();
    } catch {}
  }

  const hasValidConditions = conditions.some((c) => c.match_value.trim() !== '');
  const isSubmitDisabled = !categoryId || !hasValidConditions || isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {(mutation.error || remove.error) && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {(mutation.error ?? remove.error)?.message}
        </div>
      )}

      {/* Catégorie cible */}
      <FormField label="Catégorie cible" htmlFor="re-cat" required>
        <CategoryCombobox
          value={categoryId}
          onChange={(v) => setCategoryId(v ?? '')}
          placeholder="Choisir une catégorie…"
        />
      </FormField>

      {/* Opérateur */}
      <div>
        <p className="text-xs font-medium text-slate-700 mb-2">Opérateur entre les conditions</p>
        <div className="flex gap-3">
          {['AND', 'OR'].map((op) => (
            <label key={op} className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="radio"
                name="operator"
                value={op}
                checked={operator === op}
                onChange={() => setOperator(op)}
                className="h-4 w-4 border-slate-300 text-slate-800 focus:ring-slate-500"
              />
              <span className="text-sm text-slate-700">
                {op === 'AND' ? 'Toutes (AND)' : 'Au moins une (OR)'}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Conditions */}
      <div>
        <p className="text-xs font-medium text-slate-700 mb-2">
          Conditions{' '}
          <span className="text-slate-400 font-normal">({conditions.length}/10)</span>
        </p>
        <div className="space-y-2">
          {conditions.map((cond, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <select
                value={cond.match_type}
                onChange={(e) => updateCondition(idx, 'match_type', e.target.value)}
                className="shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                aria-label="Type de condition"
              >
                {MATCH_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                type="text"
                value={cond.match_value}
                onChange={(e) => updateCondition(idx, 'match_value', e.target.value)}
                placeholder="Valeur…"
                className="flex-1 min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                aria-label={`Valeur condition ${idx + 1}`}
              />
              <button
                type="button"
                onClick={() => removeCondition(idx)}
                disabled={conditions.length === 1}
                className="shrink-0 rounded p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 transition-colors"
                aria-label={`Supprimer la condition ${idx + 1}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        {conditions.length < 10 && (
          <button
            type="button"
            onClick={addCondition}
            className="mt-2 flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded transition-colors"
          >
            <Plus size={14} />
            Ajouter une condition
          </button>
        )}
      </div>

      {/* Priorité */}
      <FormField
        label="Priorité"
        htmlFor="re-priority"
        hint="Plus haute valeur = appliquée en premier."
      >
        <input
          id="re-priority"
          type="number"
          min="1"
          max="1000"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
        />
      </FormField>

      {/* Actions */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="flex gap-2">
          <Button type="submit" disabled={isSubmitDisabled} loading={isPending}>
            Enregistrer
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>
            Annuler
          </Button>
        </div>

        {isEdit && onDelete && (
          <div className="flex items-center gap-2">
            {confirmDelete && (
              <span className="text-xs text-red-700 font-medium">Confirmer ?</span>
            )}
            <Button
              type="button"
              variant="danger"
              className="text-xs px-3 py-1.5"
              onClick={handleDelete}
              loading={remove.isPending}
              aria-label="Supprimer la règle"
            >
              {confirmDelete ? 'Oui, supprimer' : 'Supprimer la règle'}
            </Button>
            {confirmDelete && (
              <Button
                type="button"
                variant="ghost"
                className="text-xs px-3 py-1.5"
                onClick={() => setConfirmDelete(false)}
              >
                Annuler
              </Button>
            )}
          </div>
        )}
      </div>
    </form>
  );
}
